// Kinio KMS 操作台的 react-query hooks：執行環境（mock 開關、我是誰）、總覽 / 選單用的 dashboard、家戶與組織的 plane。
import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { isKinioError } from '@kinio/web-sdk';
import { useApp } from '../../lib/AppContext';
import { useScope } from '../../lib/scope';
import type { MyOrg, ScopeHome } from '../../lib/planeApi';
import { configureKmgr, kmgrApi } from './api';
import { kmgrMockMode } from './mockMode';
import { kmgrScopeOf, sortOrgSummaries } from './model';
import type { HomeStatus, Incident, KmgrDashboard, SourceState } from './types';
import { EMPTY_AGENTS, EMPTY_COUNTS } from './wire';
import { mergePowerSummary } from './power';

/** 每次 render 設好 api.ts 的執行環境（mock 模式、mock 用的組織與家戶）；回我的 user_id。 */
export function useKmgrEnv(): { me: string; meName: string } {
  const { config, session } = useApp();
  const { orgs, homes } = useScope();
  const me = session?.user?.user_id ?? '';
  const meName = session?.user?.display_name ?? '';
  const mode = kmgrMockMode(config);
  // 在 render 裡同步設定（查詢可能在 effect 之前就發）；同一份輸入重設沒有副作用
  configureKmgr(mode, {
    me,
    meName,
    orgs: orgs.map((o) => ({ org_id: o.org_id, display_name: o.display_name, role: o.role, kind: o.kind })),
    homes: homes.filter((h) => !h.external).map((h) => ({ home_id: h.home_id, display_name: h.display_name ?? h.home_id, org_id: h.org_id, role: h.role })),
  });
  return { me, meName };
}

/** 家戶所在 plane 的 api。 */
export function useHomeApi(homeId: string | undefined): { home: ScopeHome | undefined; api: string | undefined } {
  const { homes, planes } = useScope();
  const home = homes.find((h) => h.home_id === homeId);
  return { home, api: home?.endpoints?.api ?? planes[0]?.api };
}

/** 能用 KMS 組織事務的組織：team 組織的 owner / org_admin，或個人組織（自己是 owner）。 */
export function kmgrAdminOrgs(orgs: MyOrg[]): MyOrg[] {
  return orgs.filter((o) => !o.notice && (o.role === 'owner' || o.role === 'org_admin'));
}

const EMPTY_INCIDENTS: KmgrDashboard['incidents'] = { source_state: 'ok', counts: EMPTY_COUNTS, items: [] };
const EMPTY_HOMES: KmgrDashboard['homes'] = { source_state: 'ok', total: 0, counts: {}, items: [], others_ok: 0 };

function worse(a: SourceState, b: SourceState): SourceState {
  if (a === 'unavailable' || b === 'unavailable') return a === b ? a : 'partial';
  return a === 'partial' || b === 'partial' ? 'partial' : 'ok';
}

/** 多個 plane 的 dashboard 合成一份（各區的數字相加、來源狀態取較差的）。 */
export function mergeDashboards(list: KmgrDashboard[]): KmgrDashboard | null {
  if (!list.length) return null;
  const out: KmgrDashboard = {
    checked_at: list[0].checked_at,
    incidents: { ...EMPTY_INCIDENTS, counts: { ...EMPTY_COUNTS }, items: [] },
    homes: { ...EMPTY_HOMES, counts: {}, items: [] },
    hosts: { ...EMPTY_AGENTS, source_state: 'ok' },
  };
  for (const d of list) {
    const inc = d.incidents ?? EMPTY_INCIDENTS;
    const hs = d.homes ?? EMPTY_HOMES;
    out.incidents.source_state = worse(out.incidents.source_state, inc.source_state ?? 'ok');
    for (const k of Object.keys(out.incidents.counts) as (keyof KmgrDashboard['incidents']['counts'])[]) out.incidents.counts[k] += inc.counts?.[k] ?? 0;
    out.incidents.items.push(...(inc.items ?? []));
    out.homes.source_state = worse(out.homes.source_state, hs.source_state ?? 'ok');
    out.homes.total += hs.total ?? 0;
    out.homes.others_ok += hs.others_ok ?? 0;
    out.homes.items.push(...(hs.items ?? []));
    for (const [k, v] of Object.entries(hs.counts ?? {})) out.homes.counts[k as HomeStatus['state']] = (out.homes.counts[k as HomeStatus['state']] ?? 0) + (v ?? 0);
    // 能源 NRG-K5：電源列的數字相加（最短剩餘時間取最小）
    if (d.power) out.power = mergePowerSummary(out.power ? [out.power, d.power] : [d.power]);
    if (d.hosts) {
      out.hosts.source_state = worse(out.hosts.source_state, d.hosts.source_state ?? 'ok');
      for (const k of ['total', 'online', 'offline', 'expected_offline', 'unlicensed'] as const) out.hosts[k] += d.hosts[k] ?? 0;
    }
    if (d.network) {
      const n = out.network ?? (out.network = { source_state: 'unset', unreadable_homes: 0, collectors: 0, collectors_offline: [], probes: 0, probes_up: 0, probes_failing: 0 });
      // 來源狀態取較差的：有一個 plane 讀不到就是讀不到；都沒設才是 unset
      n.source_state = d.network.source_state === 'unreadable' || n.source_state === 'unreadable' ? 'unreadable' : d.network.source_state === 'ok' || n.source_state === 'ok' ? 'ok' : 'unset';
      n.unreadable_homes += d.network.unreadable_homes;
      n.collectors += d.network.collectors;
      n.collectors_offline.push(...d.network.collectors_offline);
      n.probes += d.network.probes;
      n.probes_up += d.network.probes_up;
      n.probes_failing += d.network.probes_failing;
    }
    if (d.kinio) {
      const k = out.kinio ?? (out.kinio = { ...d.kinio, source_state: 'ok', homes: 0, unreadable: 0, devices: 0, channels: 0, cameras: 0, recorders: 0, offline_devices: 0, offline_channels: 0, storage_faults: 0, recording_gaps: 0, recording_continuous: 0 });
      k.source_state = worse(k.source_state, d.kinio.source_state);
      for (const f of ['homes', 'unreadable', 'devices', 'channels', 'cameras', 'recorders', 'offline_devices', 'offline_channels', 'storage_faults', 'recording_gaps', 'recording_continuous'] as const) k[f] += d.kinio[f];
    }
    // 依組織分組（M5-04）：各 plane 的列接起來、最後再排一次
    if (d.by_org?.length) (out.by_org ??= []).push(...d.by_org);
    // 全域關閉：任一 plane 有效就畫紅條（每個 env 自己的旗標）
    if (d.global_stop && (d.global_stop.active || !out.global_stop)) out.global_stop = d.global_stop;
    if (d.checked_at < out.checked_at) out.checked_at = d.checked_at;
  }
  if (out.by_org) out.by_org = out.by_org.length >= 2 ? sortOrgSummaries(out.by_org) : undefined;
  return out;
}

/** 只留畫面範圍內的家戶（VMS 範圍是來源 / 分享者時 kmgr 沒有對應，伺服端給 all、這裡篩）。 */
function onlyHomes(d: KmgrDashboard, ids: Set<string>): KmgrDashboard {
  const items = d.incidents.items.filter((i: Incident) => ids.has(i.home_id));
  const homes = d.homes.items.filter((h) => ids.has(h.home_id));
  const counts: Partial<Record<HomeStatus['state'], number>> = {};
  for (const h of homes) counts[h.state] = (counts[h.state] ?? 0) + 1;
  return { ...d, incidents: { ...d.incidents, items }, homes: { ...d.homes, items: homes, total: homes.length, counts, others_ok: 0 }, by_org: undefined };
}

export const DASHBOARD_REFRESH_MS = 15_000;

/** kmgr 不在（路由 404 / 功能沒開）或這個人不能用（403）：不是暫時性錯誤。 */
export function kmgrAbsent(e: unknown): boolean {
  return isKinioError(e) && (e.status === 404 || e.status === 403 || e.code === 'FEATURE_DISABLED');
}

/**
 * GET /v1/me/kmgr/dashboard（每個 plane 一次、合併）。M1 沒有 WSS 串流，固定 15 秒重抓；
 * 抓不到時保留上一次的資料（react-query），畫面用 errorUpdatedAt / dataUpdatedAt 算「更新中斷 N 分鐘」。
 */
export function useKmgrDashboard(scopeKey?: string) {
  const { planes, scope, visible } = useScope();
  useKmgrEnv();
  const key = scopeKey ?? scope;
  const ks = kmgrScopeOf(key);
  const qs = useQueries({
    queries: planes.map((p) => ({
      queryKey: ['kmgr', 'dashboard', p.api, ks.scope],
      queryFn: () => kmgrApi.dashboard(p.api, ks.scope),
      // 這個 plane 沒有 kmgr（404）或沒有權限（403）時不再輪詢：沒開 KMS 的人不要每 15 秒白打一次
      refetchInterval: (q: { state: { data?: unknown; error: unknown } }) => (!q.state.data && kmgrAbsent(q.state.error) ? false : DASHBOARD_REFRESH_MS),
      retry: (n: number, e: unknown) => !kmgrAbsent(e) && n < 1,
      staleTime: 5_000,
    })),
  });
  const ok = qs.filter((q) => q.data && typeof q.data === 'object' && 'homes' in (q.data as object)).map((q) => q.data as KmgrDashboard);
  const merged = mergeDashboards(ok);
  const visIds = useMemo(() => new Set(visible.map((h) => h.home_id)), [visible]);
  const data = merged && ks.clientFilter && !scopeKey ? onlyHomes(merged, visIds) : merged;
  const loading = qs.some((q) => q.isLoading);
  const failedAll = qs.length > 0 && qs.every((q) => q.isError && !q.data);
  const updatedAt = Math.max(0, ...qs.map((q) => q.dataUpdatedAt || 0));
  const lastErrorAt = Math.max(0, ...qs.map((q) => q.errorUpdatedAt || 0));
  const refetch = () => qs.forEach((q) => void q.refetch());
  return { data, loading, failedAll, updatedAt, lastErrorAt, refetch };
}

/**
 * 選單的「IT 監控」組：有任一家戶 kmgr_enabled 且對它有 kmgr.read 才畫（= dashboard 的 homes.total > 0，範圍「全部」）；
 * 選單「事故」旁的未處理事故數（有未處理緊急時紅色，否則灰色）。
 */
export function useKmgrNav(): { visible: boolean; unhandled: number; p1Unhandled: boolean } {
  const { data } = useKmgrDashboard('all');
  const d = data;
  if (!d || d.homes.total <= 0) return { visible: false, unhandled: 0, p1Unhandled: false };
  return { visible: true, unhandled: d.incidents.counts.unclaimed, p1Unhandled: d.incidents.counts.p1_unclaimed > 0 };
}

/** 家戶的 KMS 狀態（啟用、家戶狀態、能不能設定）。 */
export function useHomeKmgrStatus(api: string | undefined, homeId: string) {
  useKmgrEnv();
  return useQuery({
    queryKey: ['kmgr', 'home', homeId],
    queryFn: () => kmgrApi.homeStatus(api ?? '', homeId),
    enabled: !!api && !!homeId,
    retry: (n: number, e: unknown) => !kmgrAbsent(e) && n < 1,
    staleTime: 15_000,
  });
}

/** 組織的 KMS 狀態（DPA、啟用的家戶）。 */
export function useOrgKmgrStatus(org: MyOrg | undefined) {
  useKmgrEnv();
  return useQuery({
    queryKey: ['kmgr', 'org', org?.org_id],
    queryFn: () => kmgrApi.orgStatus(org!.api, org!.org_id),
    enabled: !!org,
    retry: (n: number, e: unknown) => !kmgrAbsent(e) && n < 1,
    staleTime: 30_000,
  });
}
